import { ReactNode, useCallback, useEffect, useState } from 'react';

import { ComplexMap } from '@/lib/widgets/complex/complex-map';

import { useCurrency, useCurrencyRates } from '@/lib/entities/currency';
import { RentSelectionLayout } from '@/lib/entities/page/model/rent-selection';

import { GallerySlider } from '@/lib/shared/components/Gallery';
import { GalleryTabsModal } from '@/lib/shared/components/Gallery/GalleryModal';
import { modal } from '@/lib/shared/components/Modal';
import { VideoModal } from '@/lib/shared/components/VideoModal';
import { ArrowIcon } from '@/lib/shared/icons';
import { PlayOutlinedSvg } from '@/lib/shared/icons/PlayOutlined';
import { staticResource } from '@/lib/shared/utils/resources';
import { toMoney } from '@/lib/shared/utils/to-money';

import cn from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';

import styles from './RentSelectionCard.module.scss';
import { getRentSelectionCardView } from './getRentSelectionCardView';
import {
  INCLUDED_SERVICE_ICONS,
  PAID_SERVICE_ICONS,
} from './rent-selection-services';

import { Button, CircleButton } from '../../Buttons';
import { H3, P2, P3 } from '../../Typography';

const PLACEHOLDER = '/images/placeholder_image.webp';

type RentSelectionCardProps = {
  layout: RentSelectionLayout;
};

export const RentSelectionCard = ({ layout }: RentSelectionCardProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('index');
  const {
    currency: { symbol: siteSymbol, label: siteCurrency },
  } = useCurrency();
  const { data: rates } = useCurrencyRates();
  const view = getRentSelectionCardView(
    layout,
    t,
    language,
    siteSymbol,
    siteCurrency,
    rates
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
  });
  const [failedImages, setFailedImages] = useState<Set<string>>(
    () => new Set()
  );

  const images = (layout.images ?? []).filter(
    (src) => Boolean(src) && !failedImages.has(src)
  );
  const layoutSchemes = layout.layoutSchemes?.filter(Boolean) ?? [];
  const videos = layout.videos?.filter(Boolean) ?? [];
  const hasPhotos = images.length > 0;
  const sliderImages = hasPhotos ? images : [PLACEHOLDER];
  const isAloneImage = sliderImages.length <= 1;
  const hasPoint =
    layout.point != null &&
    typeof layout.point.lat === 'number' &&
    typeof layout.point.lng === 'number';

  const { location, beachText, viewsText, bookingDates } = view;
  const {
    nightLabel,
    nightsWord,
    totalValue,
    totalSymbol,
    objectHref,
    includedServices,
    paidServices,
    commentText,
  } = view;

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, images.length]);

  const handleOpenGallery = (tabName: string, index: number) => {
    const tabList = [
      ...(hasPhotos ? ['photo'] : []),
      ...(videos.length > 0 ? ['video'] : []),
      ...(layoutSchemes.length > 0 ? ['layout'] : []),
      ...(hasPoint ? ['map'] : []),
    ];
    if (tabList.length === 0) return;

    const tabContents: ReactNode[] = [];
    if (hasPhotos) {
      tabContents.push(
        <GallerySlider
          key={images[0]}
          fullSize
          initialSlide={index}
          items={images.map((item) => staticResource(item))}
        />
      );
    }
    if (videos.length > 0) {
      tabContents.push(<VideoModal videoId={videos[0]} key={videos[0]} />);
    }
    if (layoutSchemes.length > 0) {
      tabContents.push(
        <GallerySlider
          key={layoutSchemes[0]}
          fullSize
          initialSlide={tabName === 'layout' ? index : 0}
          items={layoutSchemes.map((item) => staticResource(item))}
        />
      );
    }
    if (hasPoint && layout.point) {
      tabContents.push(
        <ComplexMap
          key="map"
          center={layout.point}
          propertyName={layout.name}
          facilities={layout.facilities ?? []}
          mapClassName={styles.galleryMap}
          filtersClassName={styles.galleryMapFilters}
          settingsBtnClassName={styles.galleryMapFiltersBtn}
        />
      );
    }

    const initialTabIndex = Math.max(tabList.indexOf(tabName), 0);
    modal.open(
      <GalleryTabsModal
        initialTabIndex={initialTabIndex}
        tabHeaders={tabList.map((tab) =>
          tab === 'map' ? t('rentPage.gallery.map') : t(tab)
        )}
        tabContents={tabContents}
      />,
      {
        fullScreen: true,
        isGalleryModal: true,
      }
    );
  };

  const handleImageError = (src: string) => {
    setFailedImages((prev) => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  };

  return (
    <li className={styles.card}>
      {layout.id ? (
        <P3 className={styles.objectId}>
          <span className={styles.objectIdLabel}>id:</span>
          <span className={styles.objectIdValue}>{layout.id}</span>
        </P3>
      ) : null}
      <div className={styles.gallery}>
        {!isAloneImage ? (
          <>
            <CircleButton
              name="scroll to next"
              theme={'dark'}
              onClick={scrollNext}
              className={cn(styles.sliderButton, styles.nextButton)}
              as={'button'}
              content={<ArrowIcon isRight />}
            />
            <CircleButton
              name="scroll to previous"
              theme={'dark'}
              onClick={scrollPrev}
              className={cn(styles.sliderButton, styles.prevButton)}
              as={'button'}
              content={<ArrowIcon />}
            />
          </>
        ) : null}
        <div className={styles.gallery_wrapper} ref={emblaRef}>
          <div className={styles.gallery_slider}>
            {sliderImages.map((image, index) => (
              <Image
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 40vw"
                key={`${image}-${index}`}
                className={styles.gallery_image}
                src={
                  hasPhotos ? staticResource(image) || PLACEHOLDER : PLACEHOLDER
                }
                width={520}
                height={732}
                alt=""
                onClick={() => handleOpenGallery('photo', index)}
                onError={() => {
                  if (hasPhotos) handleImageError(image);
                }}
              />
            ))}
          </div>
        </div>
        {videos.length > 0 ? (
          <Button
            as={'button'}
            type={'button'}
            label={t('videoReview')}
            theme={'light'}
            size={'md'}
            isIconLeft
            icon={<PlayOutlinedSvg />}
            className={styles.gallery_video}
            onClick={() => handleOpenGallery('video', 0)}
          />
        ) : null}
      </div>
      <div className={styles.footer}>
        <H3 className={styles.cardTitle}>{layout.name}</H3>
        <div className={styles.info}>
          <div className={styles.infoColumn}>
            <div className={styles.infoItem}>
              <P3>{t('location')}:</P3>
              <P2>{location}</P2>
            </div>
            {beachText ? (
              <div className={styles.infoItem}>
                <P3>{t('beach')}:</P3>
                <P2>{beachText}</P2>
              </div>
            ) : null}
            {bookingDates ? (
              <div className={styles.infoItem}>
                <P3>{t('rentSelectionPage.booking')}:</P3>
                <P2>{bookingDates}</P2>
              </div>
            ) : null}
          </div>
          <div className={styles.infoColumn}>
            {viewsText ? (
              <div className={styles.infoItem}>
                <P3>{t('view')}:</P3>
                <P2>{viewsText}</P2>
              </div>
            ) : null}
            <div className={styles.infoItem}>
              <P3>{t('bedrooms')}:</P3>
              <P2>{layout.bedrooms === 0 ? t('studio') : layout.bedrooms}</P2>
            </div>
            {layout.area ? (
              <div className={styles.infoItem}>
                <P3>{t('totalArea')}:</P3>
                <P2>
                  {layout.area} {t('prepositions.m2')}
                </P2>
              </div>
            ) : null}
          </div>
        </div>
        {includedServices.length > 0 ? (
          <div className={styles.services}>
            <P2>{t('rentSelectionPage.included')}</P2>
            <ul className={styles.chips}>
              {includedServices.map((key) => (
                <li key={key} className={styles.chip}>
                  {INCLUDED_SERVICE_ICONS[key] ? (
                    <span className={styles.chipIcon} aria-hidden>
                      {INCLUDED_SERVICE_ICONS[key]}
                    </span>
                  ) : null}
                  <span className={styles.chipLabel}>
                    {t(`rentSelectionPage.includedServices.${key}`, {
                      defaultValue: key,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {paidServices.length > 0 ? (
          <div className={styles.services}>
            <P2>{t('rentSelectionPage.paid')}</P2>
            <ul className={styles.chips}>
              {paidServices.map((item) => (
                <li key={item.key} className={styles.chip}>
                  {PAID_SERVICE_ICONS[item.key] ? (
                    <span className={styles.chipIcon} aria-hidden>
                      {PAID_SERVICE_ICONS[item.key]}
                    </span>
                  ) : null}
                  <span className={styles.chipLabel}>
                    {t(`rentSelectionPage.paidServices.${item.key}`, {
                      defaultValue: t(
                        `rentSelectionPage.includedServices.${item.key}`,
                        { defaultValue: item.key }
                      ),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {commentText ? (
          <div className={styles.comment}>
            <P2 className={styles.commentTitle}>
              {t('rentSelectionPage.comment')}
            </P2>
            <P3 className={styles.commentText}>{commentText}</P3>
          </div>
        ) : null}
        <div className={styles.bottom}>
          <div className={styles.prices}>
            {nightLabel ? (
              <H3 className={styles.price}>
                {t('rentSelectionPage.priceNight', { price: nightLabel })}
              </H3>
            ) : null}
            {totalValue != null && layout.nights ? (
              <H3 className={styles.price}>
                {t('rentSelectionPage.totalNights', {
                  count: layout.nights,
                  nightsWord,
                  price: `${totalSymbol} ${toMoney(totalValue) ?? totalValue}`,
                })}
              </H3>
            ) : totalValue != null ? (
              <H3 className={styles.price}>
                {t('rentSelectionPage.total', {
                  price: `${totalSymbol} ${toMoney(totalValue) ?? totalValue}`,
                })}
              </H3>
            ) : null}
          </div>
          {objectHref ? (
            <>
              <CircleButton
                as={Link}
                href={objectHref}
                content={<ArrowIcon isRight />}
                theme={'mixed'}
                target="_blank"
                scroll
                name={t('moreAbout')}
                className={styles.detailsButtonMobile}
              />
              <Button
                as={Link}
                href={objectHref}
                target="_blank"
                label={t('moreAbout')}
                theme={'dark'}
                size={'lg'}
                scroll
                className={styles.detailsButtonDesktop}
              />
            </>
          ) : null}
        </div>
        <P3 className={styles.currencyDisclaimer}>
          {t('rentSelectionPage.currencyDisclaimer')}
        </P3>
      </div>
    </li>
  );
};
